import { Array as EffectArray, pipe } from "effect";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import {
  isDeviceAvailable,
  resolveFallbackDeviceId,
  resolveSelectedDeviceId,
} from "../../table-call/audio-device-selection.js";
import {
  selectCurrentMicrophoneId,
  selectCurrentSpeakerId,
  selectDevicesApplying,
  selectDevicesError,
  selectPreferredMicrophoneId,
  selectPreferredSpeakerId,
  useDevicesStore,
} from "../../table-call/audio-devices.store.js";
import type { DeviceKind } from "../../table-call/audio-devices.store.js";
import { useTableCall } from "../../table-call/use-table-call.js";

export interface SettingsAudioDevicesVM {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedMicrophoneId: string | null;
  selectedSpeakerId: string | null;
  currentMicrophoneId: string | null;
  currentSpeakerId: string | null;
  isCallConnected: boolean;
  isApplying: boolean;
  error: string | null;
  setPreferredMicrophone: (id: string) => void;
  setPreferredSpeaker: (id: string) => void;
  refresh: () => Promise<void>;
}

// on windows + chrome, devices tend to have (VID:PID) suffixes, which are not present on other platforms. This should
// be used to remove them.
const WINDOWS_DEVICE_SUFFIX_REGEX = /\s+\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i;
// Some of the devices have (Bluetooth) or (USB) suffixes. They are fine in the list, but for the system default record, it's better to remove it
const LABEL_PARENTHESIS = /\s+\(.*\)\s*$/i;

const refreshBrowserDevices = async () => {
  const availableDevices = await navigator.mediaDevices.enumerateDevices();

  // biome-ignore lint/suspicious/noConsole: Debugging info
  console.log("Available devices:", availableDevices);

  const normalizeDevices = (
    devices: MediaDeviceInfo[],
    kind: MediaDeviceKind
  ): MediaDeviceInfo[] => {
    const defaultDevice = devices.find(
      (device) => device.deviceId === "default" && device.kind === kind
    );
    // Ideally, we show which device is the system default. If there is none (just sanity handler) then we just show the system default label without any device name
    const defaultDeviceLabel = (
      defaultDevice?.label
        ? ` (${defaultDevice.label.replace(LABEL_PARENTHESIS, "")})`
        : ""
    ).replace("Default - ", "");
    const systemDefaultLabel = `System default${defaultDeviceLabel}`;

    return pipe(
      devices,
      // Remove duplicated records in the list that hold "default" or "communications" deviceId. They are just aliaes for actual devices which should be listed
      // further in the list.
      // Also we filter only relevant devices (of the kind we are interested in)
      EffectArray.filter(
        (device) =>
          device.deviceId !== "communications" &&
          device.deviceId !== "default" &&
          device.kind === kind
      ),
      // Same groupId => same physical device. Some devices tend to show up multiple times (eg. different profiles)
      EffectArray.dedupeWith(
        (device, otherDevice) =>
          Boolean(device.groupId && otherDevice.groupId) &&
          device.groupId === otherDevice.groupId
      ),
      // Remove suffixes on Win + Chrome
      // I was thinking about removing prefixes (like Communications -) as well, but apparently those are translated
      EffectArray.map((device) => ({
        ...device.toJSON(),
        label: device.label.replace(WINDOWS_DEVICE_SUFFIX_REGEX, ""),
      })),
      EffectArray.prepend({
        deviceId: "default",
        groupId: "",
        kind,
        label: systemDefaultLabel,
      })
    );
  };

  return {
    microphones: normalizeDevices(availableDevices, "audioinput"),
    speakers: normalizeDevices(availableDevices, "audiooutput"),
  };
};

const isPreferredDeviceStillAvailable = ({
  preferredDeviceId,
  devices,
}: {
  preferredDeviceId: string | null;
  devices: MediaDeviceInfo[];
}) => !preferredDeviceId || isDeviceAvailable(devices, preferredDeviceId);

const recordPreferredDeviceMissing = ({
  kind,
  preferredDeviceId,
}: {
  kind: "audioinput" | "audiooutput";
  preferredDeviceId: string | null;
}) => {
  recordErrorInSentry(
    new Error(
      kind === "audioinput"
        ? "Preferred microphone missing"
        : "Preferred speaker missing"
    ),
    {
      extra: {
        kind,
        preferredDeviceId,
      },
      tags: {
        event: "preferred-device-missing",
        feature: "table-call",
      },
    }
  );
};

const reconcileUnavailablePreferredDevice = ({
  kind,
  preferredDeviceId,
  preferredDeviceStillAvailable,
  fallbackDeviceId,
  setPreferredDevice,
}: {
  kind: DeviceKind;
  preferredDeviceId: string | null;
  preferredDeviceStillAvailable: boolean;
  fallbackDeviceId: string | null;
  setPreferredDevice: (kind: DeviceKind, deviceId: string | null) => void;
}) => {
  if (preferredDeviceStillAvailable) {
    return;
  }

  recordPreferredDeviceMissing({
    kind,
    preferredDeviceId,
  });
  setPreferredDevice(kind, fallbackDeviceId);
};

export const useSettingsAudioDevices = (): SettingsAudioDevicesVM => {
  const {
    meeting,
    status,
    setPreferredMicrophone: setPreferredMicrophoneInCall,
    setPreferredSpeaker: setPreferredSpeakerInCall,
  } = useTableCall();

  const preferredMicrophoneId = useDevicesStore(selectPreferredMicrophoneId);
  const preferredSpeakerId = useDevicesStore(selectPreferredSpeakerId);
  const currentMicrophoneId = useDevicesStore(selectCurrentMicrophoneId);
  const currentSpeakerId = useDevicesStore(selectCurrentSpeakerId);
  const isApplying = useDevicesStore(selectDevicesApplying);
  const error = useDevicesStore(selectDevicesError);

  const setPreferredDevice = useDevicesStore(
    (state) => state.setPreferredDevice
  );
  const setCurrentDevice = useDevicesStore((state) => state.setCurrentDevice);
  const setLastError = useDevicesStore((state) => state.setLastError);

  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const refreshRequestIdRef = useRef(0);
  const isCallConnectedRef = useRef(false);

  const isCallConnected = status === "connected" && Boolean(meeting);

  useEffect(() => {
    isCallConnectedRef.current = isCallConnected;
    if (isCallConnected) {
      // Invalidate any pending pre-call refresh so stale results cannot commit.
      refreshRequestIdRef.current += 1;
    }
  }, [isCallConnected]);

  useEffect(
    () => () => {
      refreshRequestIdRef.current += 1;
    },
    []
  );

  const shouldDropRefreshResult = useCallback(
    (requestId: number) => requestId !== refreshRequestIdRef.current,
    []
  );

  const commitRefreshedLocalDevices = useCallback(
    ({
      microphones: nextMicrophones,
      speakers: nextSpeakers,
    }: {
      microphones: MediaDeviceInfo[];
      speakers: MediaDeviceInfo[];
    }) => {
      setMicrophones(nextMicrophones);
      setSpeakers(nextSpeakers);

      const preferredMicStillAvailable = isPreferredDeviceStillAvailable({
        devices: nextMicrophones,
        preferredDeviceId: preferredMicrophoneId,
      });
      const preferredSpeakerStillAvailable = isPreferredDeviceStillAvailable({
        devices: nextSpeakers,
        preferredDeviceId: preferredSpeakerId,
      });

      const nextCurrentMicrophoneId =
        (preferredMicStillAvailable ? preferredMicrophoneId : null) ??
        resolveFallbackDeviceId(nextMicrophones) ??
        null;
      const nextCurrentSpeakerId =
        (preferredSpeakerStillAvailable ? preferredSpeakerId : null) ??
        resolveFallbackDeviceId(nextSpeakers) ??
        null;

      reconcileUnavailablePreferredDevice({
        fallbackDeviceId: nextCurrentMicrophoneId,
        kind: "audioinput",
        preferredDeviceId: preferredMicrophoneId,
        preferredDeviceStillAvailable: preferredMicStillAvailable,
        setPreferredDevice,
      });
      reconcileUnavailablePreferredDevice({
        fallbackDeviceId: nextCurrentSpeakerId,
        kind: "audiooutput",
        preferredDeviceId: preferredSpeakerId,
        preferredDeviceStillAvailable: preferredSpeakerStillAvailable,
        setPreferredDevice,
      });

      setCurrentDevice("audioinput", nextCurrentMicrophoneId);
      setCurrentDevice("audiooutput", nextCurrentSpeakerId);
      setLastError(null);
    },
    [
      preferredMicrophoneId,
      preferredSpeakerId,
      setCurrentDevice,
      setLastError,
      setPreferredDevice,
    ]
  );

  const handleLocalRefreshFailure = useCallback(
    ({ message }: { message: string }) => {
      setLastError(message);
      recordErrorInSentry(new Error(message), {
        tags: {
          event: "audio-device-refresh-failed",
          feature: "table-call",
        },
      });
    },
    [setLastError]
  );

  const refreshLocalDevices = useCallback(async () => {
    const requestId = ++refreshRequestIdRef.current;

    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        throw new Error("media-devices-not-supported");
      }

      const { microphones: nextMicrophones, speakers: nextSpeakers } =
        await refreshBrowserDevices();

      if (shouldDropRefreshResult(requestId)) {
        return;
      }

      commitRefreshedLocalDevices({
        microphones: nextMicrophones,
        speakers: nextSpeakers,
      });
    } catch (refreshError) {
      const message =
        refreshError instanceof Error
          ? refreshError.message
          : "audio-device-refresh-failed";

      if (shouldDropRefreshResult(requestId)) {
        return;
      }

      handleLocalRefreshFailure({
        message,
      });
    }
  }, [
    commitRefreshedLocalDevices,
    handleLocalRefreshFailure,
    shouldDropRefreshResult,
  ]);

  useEffect(() => {
    let disposed = false;

    const refresh = async () => {
      await refreshLocalDevices();
    };

    const onDeviceChange = () => {
      if (disposed) {
        return;
      }

      refresh().catch((refreshError) => {
        recordErrorInSentry(refreshError);
      });
    };

    refresh().catch((refreshError) => {
      recordErrorInSentry(refreshError);
    });

    navigator.mediaDevices?.addEventListener("devicechange", onDeviceChange);

    return () => {
      disposed = true;
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        onDeviceChange
      );
    };
  }, [refreshLocalDevices]);

  const refresh = useCallback(async () => {
    await refreshLocalDevices();
  }, [refreshLocalDevices]);

  const setPreferredMicrophone = useCallback(
    (id: string) => {
      if (isCallConnectedRef.current) {
        setPreferredMicrophoneInCall(id).catch((selectionError) => {
          recordErrorInSentry(selectionError);
        });
        return;
      }
      setPreferredDevice("audioinput", id);
    },
    [setPreferredDevice, setPreferredMicrophoneInCall]
  );

  const setPreferredSpeaker = useCallback(
    (id: string) => {
      if (isCallConnectedRef.current) {
        setPreferredSpeakerInCall(id).catch((selectionError) => {
          recordErrorInSentry(selectionError);
        });
        return;
      }
      setPreferredDevice("audiooutput", id);
    },
    [setPreferredDevice, setPreferredSpeakerInCall]
  );

  const selectedMicrophoneId = resolveSelectedDeviceId({
    currentDeviceId: currentMicrophoneId,
    devices: microphones,
    preferredDeviceId: preferredMicrophoneId,
  });
  const selectedSpeakerId = resolveSelectedDeviceId({
    currentDeviceId: currentSpeakerId,
    devices: speakers,
    preferredDeviceId: preferredSpeakerId,
  });

  return useMemo(
    () => ({
      currentMicrophoneId,
      currentSpeakerId,
      error,
      isApplying,
      isCallConnected,
      microphones,
      refresh,
      selectedMicrophoneId,
      selectedSpeakerId,
      setPreferredMicrophone,
      setPreferredSpeaker,
      speakers,
    }),
    [
      microphones,
      speakers,
      selectedMicrophoneId,
      selectedSpeakerId,
      currentMicrophoneId,
      currentSpeakerId,
      isCallConnected,
      isApplying,
      error,
      setPreferredMicrophone,
      setPreferredSpeaker,
      refresh,
    ]
  );
};
